import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { ValidationError, toDomainError } from '@domain/errors';

interface ToastState {
  message: string;
  tone: 'info' | 'error';
}

interface ToastApi {
  notify: (message: string) => void;
  notifyError: (error: unknown) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((message: string, tone: ToastState['tone']) => {
    setToast({ message, tone });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      notify: (message) => show(message, 'info'),
      notifyError: (error) => {
        const domainError = toDomainError(error);
        if (domainError instanceof ValidationError && Object.keys(domainError.fields).length > 0) {
          const lines = Object.entries(domainError.fields)
            .map(([, msg]) => msg)
            .join(' · ');
          show(`${domainError.message} — ${lines}`, 'error');
        } else {
          show(domainError.message, 'error');
        }
      },
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast ? (
        <div
          className={`bbm-toast${toast.tone === 'error' ? ' bbm-toast--error' : ''}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast doit être utilisé dans un <ToastProvider>.');
  return context;
}
